import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { Page } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { pagePath } from '../schemas/page-rules';

import { PageStatusBadge } from './page-status-badge';

/** Column model of the pages table; `meta` ties columns to the list's sorts and filters. */
export function usePageColumns(storeId: string) {
  const { t } = useTranslation('pages');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<Page>>>(
    () => [
      {
        id: 'title',
        header: t('columns.page'),
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col">
            <Link
              to="/s/$storeId/content/pages/$pageId"
              params={{ storeId, pageId: row.original.id }}
              className="truncate font-medium hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.title}
            </Link>
            <span className="text-muted-foreground truncate font-mono text-xs">
              {pagePath(row.original.handle)}
            </span>
          </div>
        ),
        meta: { label: t('columns.page'), sortField: 'title', lockVisibility: true },
      },
      {
        id: 'template',
        header: t('columns.template'),
        cell: ({ row }) => t(`template.${row.original.template}`),
        meta: { label: t('columns.template'), filterKey: 'template' },
      },
      {
        id: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <PageStatusBadge status={row.original.status} />,
        meta: { label: t('columns.status'), filterKey: 'status' },
      },
      {
        id: 'seo',
        header: t('columns.seo'),
        cell: ({ row }) => {
          const count = row.original.resolvedSeo.warnings.length;
          return count === 0 ? (
            <span className="text-muted-foreground text-sm">{t('columns.seoOk')}</span>
          ) : (
            <span className="text-warning-foreground flex items-center gap-1 text-sm">
              <AlertTriangle className="text-warning size-3.5" aria-hidden />
              {t('columns.seoWarnings', { count })}
            </span>
          );
        },
        meta: { label: t('columns.seo') },
      },
      {
        id: 'updatedAt',
        header: t('columns.updated'),
        cell: ({ row }) => (
          <span title={format.utc(row.original.updatedAt)} className="whitespace-nowrap">
            {format.relative(row.original.updatedAt)}
          </span>
        ),
        meta: { label: t('columns.updated'), sortField: 'updatedAt', filterKey: 'updatedAt' },
      },
    ],
    [format, storeId, t],
  );
}
